import { z } from 'zod';

/**
 * Quality HQ v1 — dynamic evidence, automation value, test intelligence,
 * adaptive review proposals, and the learn-from-prod bump.
 *
 * Pure helpers live here so the CLI, API, and unit tests share one
 * definition of "run this, skip that" and of the value formula.
 */

const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
type Severity = (typeof SEVERITIES)[number];

export const QUALITY_FLAKE_BUDGET = 0.25;
export const QUALITY_INCIDENT_WINDOW_DAYS = 14;
export const QUALITY_VALUE_WINDOW_DAYS = 30;

export const QUALITY_EVIDENCE_REASONS = [
  'tia_match',
  'tia_fallback',
  'gating_risk',
  'recent_incident',
  'uncovered_risk',
  'flake_over_budget',
  'tia_miss',
  'low_value',
] as const;
export type QualityEvidenceReason = (typeof QUALITY_EVIDENCE_REASONS)[number];

export const QUALITY_EVIDENCE_ACTIONS = ['run', 'skip'] as const;
export type QualityEvidenceAction = (typeof QUALITY_EVIDENCE_ACTIONS)[number];

export const QUALITY_VALUE_ACTIONS = ['add', 'keep', 'quarantine', 'remove'] as const;
export type QualityValueAction = (typeof QUALITY_VALUE_ACTIONS)[number];

export const QualityEvidenceFlowSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  action: z.enum(QUALITY_EVIDENCE_ACTIONS),
  reasons: z.array(z.enum(QUALITY_EVIDENCE_REASONS)),
  tiaScore: z.number().min(0).max(1).nullable(),
  riskIds: z.array(z.string().uuid()),
  flakeRate: z.number().min(0).max(1).nullable(),
  lastIncidentAt: z.string().nullable(),
  whyExists: z.string(),
});
export type QualityEvidenceFlow = z.infer<typeof QualityEvidenceFlowSchema>;

export const QualityEvidencePlanSummarySchema = z.object({
  runCount: z.number().int().nonnegative(),
  skipCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  headline: z.string(),
  nightlyFullSuite: z.literal(false),
  commitSha: z.string().nullable(),
  hasDiff: z.boolean(),
  runPreview: z.array(QualityEvidenceFlowSchema),
  skipPreview: z.array(QualityEvidenceFlowSchema),
});
export type QualityEvidencePlanSummary = z.infer<typeof QualityEvidencePlanSummarySchema>;

export const QualityAutomationValueRowSchema = z.object({
  flowId: z.string().uuid().nullable(),
  flowName: z.string().nullable(),
  riskId: z.string().uuid().nullable(),
  riskTitle: z.string().nullable(),
  action: z.enum(QUALITY_VALUE_ACTIONS),
  score: z.number().nonnegative(),
  frequency: z.number().nonnegative(),
  criticality: z.number().min(0).max(1),
  catchRate: z.number().min(0).max(1),
  maintenance: z.number().positive(),
  detail: z.string(),
});
export type QualityAutomationValueRow = z.infer<typeof QualityAutomationValueRowSchema>;

export const QualityAutomationValueSummarySchema = z.object({
  add: z.number().int().nonnegative(),
  keep: z.number().int().nonnegative(),
  quarantine: z.number().int().nonnegative(),
  remove: z.number().int().nonnegative(),
  preview: z.array(QualityAutomationValueRowSchema),
});
export type QualityAutomationValueSummary = z.infer<typeof QualityAutomationValueSummarySchema>;

export const QualityAdaptiveProposalSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  kind: z.literal('locator_drift'),
  detail: z.string(),
  lastHealedAt: z.string().nullable(),
  healCount: z.number().int().nonnegative(),
});
export type QualityAdaptiveProposal = z.infer<typeof QualityAdaptiveProposalSchema>;

export const QualityLearnResultSchema = z.object({
  bumped: z.boolean(),
  riskId: z.string().uuid().nullable(),
  from: z.enum(SEVERITIES).nullable(),
  to: z.enum(SEVERITIES).nullable(),
});
export type QualityLearnResult = z.infer<typeof QualityLearnResultSchema>;

export const AckQualityFindingResponseSchema = z.object({
  ok: z.literal(true),
  learned: QualityLearnResultSchema.optional(),
});
export type AckQualityFindingResponse = z.infer<typeof AckQualityFindingResponseSchema>;

const SEVERITY_RANK: Record<Severity, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

/** Return `proposed` when it is strictly harsher than `current`. */
export function harsherSeverity(current: Severity, proposed: Severity): Severity | null {
  return SEVERITY_RANK[proposed] > SEVERITY_RANK[current] ? proposed : null;
}

export interface EvidenceCandidate {
  flowId: string;
  flowName: string;
  /** `null` means no diff was supplied — TIA does not vote. */
  tiaScore: number | null;
  tiaFallback: boolean;
  gatingRiskIds: string[];
  riskIds: string[];
  uncoveredGating: boolean;
  flakeRate: number | null;
  lastIncidentAt: string | null;
  whyExists: string;
}

function withinDays(iso: string | null, nowMs: number, windowDays: number): boolean {
  if (!iso) return false;
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return false;
  return nowMs - then <= windowDays * 24 * 60 * 60 * 1000;
}

/**
 * Selector = TIA ∩ risk severity ∩ flake budget ∩ last incident.
 *
 * A flow is selected when any of these fire, unless flake budget is
 * blown *and* the flow is not gating and has no recent incident:
 *
 *   - TIA score above zero (the diff touched this flow)
 *   - no graph yet (`tiaFallback`) — we refuse to silently drop it
 *   - linked to a critical/high risk
 *   - that gating risk still has no passing evidence
 *   - a production incident on a linked risk inside the window
 *
 * When no diff is posted (`tiaScore === null` for every flow), TIA
 * does not vote and we still select only gating + incident flows —
 * that is "test only what matters" without a commit.
 */
export function selectEvidenceFlows(
  candidates: EvidenceCandidate[],
  opts: { nowMs?: number; incidentWindowDays?: number; flakeBudget?: number } = {},
): QualityEvidenceFlow[] {
  const nowMs = opts.nowMs ?? Date.now();
  const windowDays = opts.incidentWindowDays ?? QUALITY_INCIDENT_WINDOW_DAYS;
  const budget = opts.flakeBudget ?? QUALITY_FLAKE_BUDGET;

  return candidates.map((c) => {
    const reasons: QualityEvidenceReason[] = [];
    const tiaHit = c.tiaScore !== null && c.tiaScore > 0;
    const incident = withinDays(c.lastIncidentAt, nowMs, windowDays);
    const flakeOver = c.flakeRate !== null && c.flakeRate > budget;
    const gating = c.gatingRiskIds.length > 0 || c.uncoveredGating;

    if (tiaHit) reasons.push('tia_match');
    if (c.tiaFallback) reasons.push('tia_fallback');
    if (gating) reasons.push('gating_risk');
    if (c.uncoveredGating) reasons.push('uncovered_risk');
    if (incident) reasons.push('recent_incident');
    if (c.tiaScore === 0 && !c.tiaFallback) reasons.push('tia_miss');
    if (flakeOver) reasons.push('flake_over_budget');

    const votesRun = tiaHit || c.tiaFallback || gating || incident;
    const flakeBlocks = flakeOver && !gating && !incident;
    const action: QualityEvidenceAction = votesRun && !flakeBlocks ? 'run' : 'skip';
    if (action === 'skip' && !reasons.includes('tia_miss') && !flakeOver) {
      reasons.push('low_value');
    }

    return {
      flowId: c.flowId,
      flowName: c.flowName,
      action,
      reasons,
      tiaScore: c.tiaScore,
      riskIds: c.riskIds,
      flakeRate: c.flakeRate,
      lastIncidentAt: c.lastIncidentAt,
      whyExists: c.whyExists,
    };
  });
}

export function evidencePlanHeadline(runCount: number, skipCount: number, hasDiff: boolean): string {
  const total = runCount + skipCount;
  if (total === 0) return 'No flows in this project yet.';
  const suffix = hasDiff ? 'for this change' : 'until a diff is posted';
  return `Run ${runCount}, skip ${skipCount} ${suffix}. Nightly full suite stays opt-in.`;
}

export function criticalityFrom(opts: {
  revenueTier: 'high' | 'med' | 'low' | null;
  maxRiskSeverity: Severity | null;
}): number {
  const fromRisk =
    opts.maxRiskSeverity === 'critical'
      ? 1
      : opts.maxRiskSeverity === 'high'
        ? 0.85
        : opts.maxRiskSeverity === 'medium'
          ? 0.5
          : opts.maxRiskSeverity === 'low'
            ? 0.25
            : 0;
  const fromTier =
    opts.revenueTier === 'high' ? 1 : opts.revenueTier === 'med' ? 0.55 : opts.revenueTier === 'low' ? 0.2 : 0;
  return Math.max(fromRisk, fromTier, 0.1);
}

/**
 * Value = (execution frequency × business criticality × catch-rate) / maintenance.
 *
 * `frequency` is runs in the window. `catchRate` is defects attributed
 * to the flow divided by runs (0 if it has never caught anything).
 * `maintenance` is 1 + flake rate + heals-per-run so a noisy test
 * costs more than a quiet one.
 */
export function scoreAutomationValue(opts: {
  runs: number;
  defects: number;
  flakeRate: number | null;
  healCount: number;
  criticality: number;
}): { score: number; frequency: number; catchRate: number; maintenance: number } {
  const frequency = Math.max(0, opts.runs);
  const catchRate = frequency === 0 ? 0 : Math.min(1, opts.defects / frequency);
  const flake = opts.flakeRate ?? 0;
  const healsPerRun = frequency === 0 ? 0 : opts.healCount / frequency;
  const maintenance = 1 + flake + healsPerRun;
  const score = (frequency * opts.criticality * catchRate) / maintenance;
  return { score, frequency, catchRate, maintenance };
}

export function decideAutomationAction(opts: {
  score: number;
  flakeRate: number | null;
  gating: boolean;
  flakeBudget?: number;
}): QualityValueAction {
  const budget = opts.flakeBudget ?? QUALITY_FLAKE_BUDGET;
  if (opts.flakeRate !== null && opts.flakeRate > budget) return 'quarantine';
  if (!opts.gating && opts.score === 0) return 'remove';
  return 'keep';
}

export function whyExistsLine(opts: { riskTitles: string[]; storyTitle: string | null }): string {
  if (opts.riskTitles.length > 0) {
    return `Protects ${opts.riskTitles.join(', ')}.`;
  }
  if (opts.storyTitle) {
    return `Traces to story “${opts.storyTitle}”.`;
  }
  return 'No linked requirement or risk. Candidate to remove.';
}
